import { useHybridData } from "./useHybridData";
import { supabase } from "@/integrations/supabase/client";
import type { Hospital } from "@/types/schema";
import hospitalsIndex from "@/data/generated/hospitals-index.json";
import { useMemo } from "react";

// Load all individual hospital files for static detail views
const hospitalDetails = import.meta.glob('@/data/generated/hospitals/*.json', { eager: true });

// Helper to look up static detail by slug
const getStaticHospital = (slug: string): Hospital | null => {
  const match = Object.entries(hospitalDetails).find(([path]) => path.includes(`/${slug}.json`));
  return match ? (match[1] as { default: Hospital }).default : null;
};

// Fetch only NEW/UPDATED hospitals since build
const fetchNewHospitals = async (lastBuildDate: string) => {
  const { data, error } = await supabase
    .from("hospitals")
    .select("*")
    .gt("updated_at", lastBuildDate) // Use updated_at to catch edits too
    .order("is_featured", { ascending: false });

  if (error) {
    console.error("Error fetching new/updated hospitals:", error);
    return null;
  }
  return data as Hospital[];
};

export function useHospitals() {
  const { data, isStatic, isCheckingUpdates } = useHybridData<Hospital>({
    resourceName: "Hospitals",
    staticData: hospitalsIndex as unknown as Hospital[], // Index is lightweight, but compatible enough
    fetchDynamic: fetchNewHospitals,
    queryKey: ["hospitals"],
    sortFn: (a, b) => {
      // Sort by featured then rating
      if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1;
      return (b.rating || 0) - (a.rating || 0);
    },
    skipUpdates: true,
  });

  return {
    data,
    isLoading: false,
    isStatic,
    isCheckingUpdates
  };
}

export function useHospitalBySlug(slug: string | undefined) {
  // 1. Try to find in static detailed files (Best Case: Full content instant)
  const staticDetail = useMemo(() => 
    slug ? getStaticHospital(slug) : null, [slug]
  );

  // 2. Fallback: Try to find in the index (Lightweight)
  const { data: allHospitals } = useHospitals();
  const foundInList = useMemo(() => {
    return allHospitals?.find(h => h.slug === slug);
  }, [allHospitals, slug]);

  const initialData = staticDetail || foundInList;

  const { data: fullData, isCheckingUpdates: isLoading } = useHybridData<Hospital>({
    resourceName: `Hospital: ${slug}`,
    staticData: initialData ? [initialData] : [],
    fetchDynamic: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("hospitals")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      
      if (error) {
        console.error(`Error fetching hospital ${slug}:`, error);
        return null; // Don't throw, just return null so hybrid hook handles it
      }
      return data ? [data as Hospital] : [];
    },
    queryKey: ["hospital", slug || ""],
    skipUpdates: true,
  });

  const hospital = fullData?.[0] || initialData;

  return {
    data: hospital,
    isLoading: !hospital && isLoading,
  };
}

export function useFeaturedHospitals(limit: number = 4) {
  const { data } = useHospitals();
  
  const featured = useMemo(() => {
    return data
      .filter(h => h.is_featured)
      .slice(0, limit);
  }, [data, limit]);

  return {
    data: featured,
    isLoading: false
  };
}
